import {
  MODAL_BLOCK,
  MODAL_BLOCK_CONTENT_VIEW,
} from '@components/App/Modal/constants'
import type { ID } from '@octopus-do/octopus-project-core'
import { createSelector } from '@reduxjs/toolkit'
import { selectThreads } from '@store/reducers/threads/selectors'
import type { Thread } from '@store/reducers/threads/types'
import type { AppState } from '@store/types'

import { authSelector } from '@/hooks/useAuth'
import type { Comment } from '@/types'
import { BASELINE_TARGET } from './constants'
import type { CommentReadsState, UnreadMap } from './types'

const NONE: Set<ID> = new Set()
const EMPTY: UnreadMap = { threads: NONE, targets: NONE, places: NONE }
const NO_READS: CommentReadsState = {}
const NO_THREADS: Thread[] = []
const NO_COMMENTS: Comment[] = []

// The fallbacks are for a store built by hand in a test, where a slice this
// feature reads may be left out; the app always has all three
export const selectCommentReads = (state: AppState): CommentReadsState =>
  state.manager.commentReads ?? NO_READS

const selectThreadsList = (state: AppState): Thread[] =>
  selectThreads(state) ?? NO_THREADS

const selectCommentsList = (state: AppState): Comment[] =>
  state.manager.comments ?? NO_COMMENTS

const selectRelations = (state: AppState) => state.relations.dependencies

// A guest has no read state, so nothing is unread for them
const selectViewerId = createSelector([authSelector], (auth) =>
  auth.authenticated ? auth.id : null
)

export const readSince = (reads: CommentReadsState, target: ID): number =>
  reads[target] ?? reads[BASELINE_TARGET] ?? 0

interface Authored {
  owner: ID | null
  createdAt: number
}

const isUnread = (item: Authored, since: number, viewerId: ID) =>
  item.owner !== viewerId && item.createdAt > since

// Newest foreign item under a target: the thread pin itself when the target is
// a thread, plus every comment attached to it. Null when there is nothing to
// read there, which is also the case for one's own comments alone.
export const selectLatestForeignCreatedAt =
  (target: ID) =>
  (state: AppState): number | null => {
    const viewerId = selectViewerId(state)

    if (viewerId === null) {
      return null
    }

    const thread = selectThreadsList(state).find((t) => t.uuid === target)
    const items: Authored[] = selectCommentsList(state).filter(
      (c: Comment) => c.commentable === target
    )

    const foreign = [...items, ...(thread ? [thread] : [])]
      .filter((item) => item.owner !== viewerId)
      .map((item) => item.createdAt)

    return foreign.length ? Math.max(...foreign) : null
  }

// One pass over comments and threads; memoized so every pin, block and page
// card reads a Set instead of filtering the comments array on render
export const selectUnread = createSelector(
  [
    selectThreadsList,
    selectCommentsList,
    selectCommentReads,
    selectViewerId,
    selectRelations,
  ],
  (threads, comments, reads, viewerId, dependencies): UnreadMap => {
    // Until the server has answered there is no baseline, and with no baseline
    // "unread" would mean every comment ever written — better to show nothing
    if (viewerId === null || reads[BASELINE_TARGET] === undefined) {
      return EMPTY
    }

    const targets = new Set<ID>()

    comments.forEach((comment: Comment) => {
      const { commentable } = comment

      if (isUnread(comment, readSince(reads, commentable), viewerId)) {
        targets.add(commentable)
      }
    })

    // A pin whose page is gone is neither drawn nor listed, so it must not
    // light the toolbar either — the same rule the panel applies
    const unreadThreads = threads.filter(
      ({ uuid, resolved, owner, createdAt, commentable }) =>
        !resolved &&
        commentable in dependencies &&
        (targets.has(uuid) ||
          isUnread({ owner, createdAt }, readSince(reads, uuid), viewerId))
    )

    // A target is a place — a block or a page — unless it is a thread's uuid
    // (a reply lands under the pin's key), and only while the block or page
    // still exists in the tree
    const pinIds = new Set(threads.map((t) => t.uuid))
    const places = new Set(
      [...targets].filter((id) => !pinIds.has(id) && id in dependencies)
    )

    return {
      threads: new Set(unreadThreads.map((t) => t.uuid)),
      targets,
      places,
    }
  }
)

// The one signal outside the panel: the toolbar button lights up when any
// pin, block or page has something unread
export const selectHasUnread = createSelector(
  [selectUnread],
  ({ threads, places }) => threads.size > 0 || places.size > 0
)

// What the viewer is looking at right now: the opened pin, the block modal or
// the page content view. These are the places a read mark is earned.
export const selectOpenTargets = ({ canvas, ui }: AppState): ID[] => {
  const modalTarget =
    (ui.modalType === MODAL_BLOCK && ui.modalProps.id) ||
    (ui.modalType === MODAL_BLOCK_CONTENT_VIEW && ui.modalProps.nodeId) ||
    null

  return [canvas.selectedThread, modalTarget].filter((target): target is ID =>
    Boolean(target)
  )
}
